import { usePage } from '@inertiajs/vue3'
import { computed } from 'vue'

import { parseDate } from '@ja/core-ui/lib/date'
import { useApiQuery } from '@ja/portal-query'

export interface Message {
  id: string
  content: string
  sender?: { id: string; name: string }
  created_at: string
}

export interface Conversation {
  id: string
  type: string
  title: string
  subtitle?: string
  communication_id?: string
  communication_recipient_id?: string
  communication?: {
    id: string
    title: string
  }
  owner?: {
    id: string
    name: string
  }
  latest_message: Message | null
  messages_count?: number
  updated_at: string
}

export type { CommunicationItem as InboxItem } from '../types/communication'
import type { CommunicationItem as InboxItem } from '../types/communication'

export type UnifiedMessage =
  | ({ unifiedType: 'conversation'; timestamp: string } & Conversation)
  | ({ unifiedType: 'inbox'; timestamp: string; latest_message?: Message | null; messages_count?: number } & InboxItem)

export function useConversations() {
  const page = usePage()

  const userId = computed(() => {
    const auth = (page.props as Record<string, unknown>).auth as { user?: { id: string } } | undefined
    return auth?.user?.id
  })

  // Fetch Conversations
  const {
    data: convData,
    isLoading: isConvLoading,
    refetch: refetchConvs,
  } = useApiQuery<{ data: Conversation[] }>(
    ['conversations', 'list'],
    '/api/conversations',
    {},
    {
      enabled: computed(() => !!userId.value),
    },
  )

  // Fetch Inbox Items (Communications)
  const {
    data: inboxData,
    isLoading: isInboxLoading,
    refetch: refetchInbox,
  } = useApiQuery<{ data: InboxItem[] }>(
    ['communications', 'inbox', 'list'],
    '/api/communications',
    {},
    {
      enabled: computed(() => !!userId.value),
    },
  )

  const unifiedMessages = computed(() => {
    const conversations = convData.value?.data ?? []
    const inboxItems = inboxData.value?.data ?? []

    // 1. Map inbox items and "regroup" their conversations
    const mappedInbox = inboxItems.map((item) => {
      // Find matching conversation if any
      const matchingConv = item.conversation_id 
        ? conversations.find(c => c.id === item.conversation_id)
        : conversations.find(c => c.communication_id === item.communication_id);

      return {
        ...item,
        unifiedType: 'inbox' as const,
        // Use conversation timestamp if it exists and is newer
        timestamp: (() => {
          const convTs = matchingConv ? parseDate(matchingConv.updated_at)?.getTime() ?? 0 : 0
          const itemTs = parseDate(item.sent_at || item.created_at)?.getTime() ?? 0
          return matchingConv && convTs > itemTs
            ? matchingConv.updated_at
            : (item.sent_at || item.created_at)
        })(),
        // Pass through latest message from conversation for display
        latest_message: matchingConv?.latest_message || null,
        messages_count: matchingConv?.messages_count ?? 0,
      }
    })

    // 2. Map conversations that are NOT already represented in the inbox
    // (Direct Messages, or conversations where the user is a responder but NOT a recipient)
    const mappedConvs = conversations
      .filter(c => !inboxItems.some(item => 
        item.communication_id === c.communication_id || 
        item.conversation_id === c.id
      ))
      .map((c) => ({
        ...c,
        unifiedType: 'conversation' as const,
        timestamp: c.updated_at,
      }))

    // Combine and sort by timestamp
    return [...mappedConvs, ...mappedInbox].sort(
      (a, b) =>
        (parseDate(b.timestamp)?.getTime() ?? 0) - (parseDate(a.timestamp)?.getTime() ?? 0),
    )
  })

  const isLoading = computed(() => isConvLoading.value || isInboxLoading.value)

  const refresh = () => {
    void refetchConvs()
    void refetchInbox()
  }

  return {
    conversations: unifiedMessages,
    isLoading,
    refresh,
  }
}
